import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import axios from "axios";
import { toast } from "react-toastify";
import { displayPatientName } from "../../utils/displayPatientName";

import {
  PAYMENT_HEADER_BG,
  ACCENT,
  ACCENT_DARK,
  SURFACE,
  LABEL_COLOR,
  BORDER,
  BRAND,
  BRAND_LIGHT,
  SelectOptions,
  leftRightCheckboxFields,
  inputSx,
  labelSx,
  rowSx,
} from "./CaseSheetModalComponents";

import DiabetesTab from "./DiabetesTab";
import DiabeticFootTab from "./DiabeticFootTab";
import HistoryTab from "./HistoryTab";
import { initalMedicalBackgroundData } from "../doctor-console/initialData/initialData";
import showConfirmDialog from "../../components/ShowConfirmDialog/ShowConfirmDialog";


const DRAFT_DB_NAME = "caseSheetDraftsDB";
const DRAFT_STORE_NAME = "caseSheetDrafts";
const CASE_SHEET_DRAFT_KEY = "CaseSheetModal";

const openDraftDB = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DRAFT_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DRAFT_STORE_NAME)) {
        db.createObjectStore(DRAFT_STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const getDraftRecord = async (key) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readonly");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

const upsertDraftRecord = async (record) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.put(record);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

const clearDraftRecord = async (key) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.put({ key, data: null, patientId: null, updatedAt: null });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

const isMedicalBackgroundNewAllEmpty = (mb) => {
  if (!mb || typeof mb !== "object") return true;
  return Object.values(mb).every((section) => {
    if (!section || typeof section !== "object") return true;
    const n = section.new;
    if (n == null) return true;
    if (Array.isArray(n)) return n.length === 0;
    if (typeof n === "object") return Object.keys(n).length === 0;
    return false;
  });
};

const isFormDataAllEmpty = (data) => {
  if (!data || typeof data !== "object") return true;
  return Object.keys(data).every((k) => {
    const val = data[k];
    if (val === "" || val === null || val === undefined || val === false) return true;
    if (typeof val === "object" && !Array.isArray(val)) {
        return Object.values(val).every(v => v === "" || v === null || v === undefined || v === false);
    }
    return false;
  });
};

const isCaseSheetDraftContentEmpty = (formData, medicalBackground) => {
  return isFormDataAllEmpty(formData) && isMedicalBackgroundNewAllEmpty(medicalBackground);
};

// ─── Constants & Initial State ──────────────────────────────────────────────

const initialFormData = {
  medical_history: "",
  allergies: "",
  first_complaints: "",
  current_treatment: "",
  previous_treatment: "",
  family_history: "",
  menstrual_history: "",
  // review: "",
  // smoking: "",
  // drinking: "",
  // tobacco: "",
  // exercise: "",
  // description: "",
  // diet: "",
  diabetes_type: "",
  diabetes_onset_time: "",
  diabetes_onset_from: "",
  hypoglycemia: "",
  diabetic_foot: "",
  peripheral_neuropathy: "",
  peripheral_vascular_disease: "",
  nephropathy: "",
  ckd: "",
  diabetic_retinopathy: "",
  hypertension: "",
  hypertension_onset_time: "",
  lipid_disorders: "",
  other_ailments: "",
  skin_normal: false,
  skin_hydration: "",
  skin_stretchability: "",
  skin_texture: "",
  foot_examination: "",
  advice: "",
  left_foot_risk: "",
  right_foot_risk: "",
  previous_ulcer: { left: false, right: false },
  previous_amputation: { left: false, right: false },
  absent_pedal_pulses: { left: false, right: false },
  active_ulcer: { left: false, right: false },
  callus: { left: false, right: false },
  blisters: { left: false, right: false },
  fissures: { left: false, right: false },
  neuropathy: { left: false, right: false },
  corn: { left: false, right: false },
  nails_thickened: { left: false, right: false },
  nails_ingrown: { left: false, right: false },
  nails_fungal: { left: false, right: false },
  deformities_claw_toes: { left: false, right: false },
  deformities_hammer_toes: { left: false, right: false },
  deformities_hallux_valgus: { left: false, right: false },
  deformities_flat_foot: { left: false, right: false },
  deformities_charcot_foot: { left: false, right: false },
  infection_redness: { left: false, right: false },
  infection_pus: { left: false, right: false },
  infection_swelling: { left: false, right: false },
  quantitative_avg_vpt: { left: "", right: "" },
  foot_normal: false,
  dental_normal: false,
  teeth_status: "",
  gums_status: "",
  // duration: "",
  // symptoms: "",
  // progression: "",
  // drug_allergies: "",
  // food_allergies: "",
  surgeries_history: "",
  vaccination_history: "",
  drug_history: "",
  addiction_history: "",
  fasting_blood_sugar: "",
  post_prandial_blood_sugar: "",
  hba1c: "",
  lipid_profile: "",
  liver_function_tests: "",
  kidney_function_tests: "",
  thyroid_profile: "",
  cbc: "",
  fib_4: "",
  ucar: "",
  other_investigations: "",
  primary_diagnosis: "",
  secondary_diagnosis: "",
  patient_motivation: "",
  // current_medications: "",
  // medication_compliance: "",
  // medication_side_effects: "",
  lifestyle_modifications: "",
  diet_plan: "",
  exercise_plan: "",
  treatment_medications: "",
  // next_visit: "",
  // monitoring_plan: "",
  cardiovascular_risk: "",
  metabolic_syndrome: "",
  history_of_present_illness: ""
};

// ─── Data Normalization Helpers ──────────────────────────────────────────────

const normalizeIncomingData = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  const raw = payload.data || payload.case_sheet || payload.caseSheet || payload;
  return Array.isArray(raw) ? (raw[0] ?? {}) : raw;
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.toLowerCase();
    return v === "true" || v === "yes" || v === "1";
  }
  return Boolean(value);
};

const buildMergedFormData = (incomingData) => {
  const merged = { ...initialFormData };
  const history = incomingData?.history || {};
  
  Object.keys(initialFormData).forEach((key) => {


    const fallbackValue = initialFormData[key];
    // Check main object
    const incoming = incomingData?.[key];
    
    if (incoming === undefined || incoming === null) return;
    
    if (key === "quantitative_avg_vpt") {
      merged[key] = {
        left: String(incoming?.left ?? fallbackValue.left ?? ""),
        right: String(incoming?.right ?? fallbackValue.right ?? ""),
      };
      return;
    }
    if (leftRightCheckboxFields.includes(key)) {
      merged[key] = {
        left: toBool(incoming?.left ?? false),
        right: toBool(incoming?.right ?? false),
      };
      return;
    }
    merged[key] = incoming;
  });
  return merged;
};

const normalizeLeftRightPayload = (data) => {
  const cleaned = { ...data };
  leftRightCheckboxFields.forEach((field) => {
    const val = cleaned[field];
    if (val && typeof val === "object") {
      const newVal = {};
      if (val.left) newVal.left = true;
      if (val.right) newVal.right = true;
      cleaned[field] = newVal;
    }
  });
  return cleaned;
};

// ─── Error state ──────────────────────────────────────────────────────────────

function FetchErrorState({ message, onRetry }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        py: 10,
      }}
    >
      <Typography
        sx={{ fontSize: 14, fontWeight: 600, color: "#444", textAlign: "center", px: 4 }}
      >
        {message}
      </Typography>
      {onRetry && (
        <Button
          variant="outlined"
          size="small"
          onClick={onRetry}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: BRAND,
            color: BRAND,
          }}
        >
          Retry
        </Button>
      )}
    </Box>
  );
}

// ─── Tab Identifiers ─────────────────────────────────────────────────────────

const SHEET_TAB_DIABETES = "diabetes";
const SHEET_TAB_FOOT = "foot";
const SHEET_TAB_HISTORY = "history";

// ─── Main CaseSheetModal Component ──────────────────────────────────────────

function CaseSheetModal({ open, onClose, appointment, patientData, onAfterSave }) {
  const [formData, setFormData] = useState(initialFormData);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSheetTab, setActiveSheetTab] = useState(SHEET_TAB_HISTORY);
  const [medicalBackground, setMedicalBackground] = useState(initalMedicalBackgroundData);
  const hasHydratedDraftRef = useRef(false);
  const isDirtyRef = useRef(false);

  const updateMedicalBackground = (type, data) => {
    isDirtyRef.current = true;
    setMedicalBackground((prev) => ({ ...prev, [type]: data }));
  };


  const backendUrl = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken]
  );

  const patientId = appointment?.patient;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchCaseSheet = useCallback(async () => {
    if (!patientId) {
      setFormData(initialFormData);
      setFetchError("Patient ID not found for selected appointment.");
      return;
    }
    setIsFetching(true);
    setFetchError(null);
    try {
      const diabeticProfilePromise = axios.get(
        `${backendUrl}create_patient_diabetic_profile/?patient_id=${encodeURIComponent(patientId)}`,
        { headers }
      );
      
      const patientHistoryPromise = axios.post(
        `${backendUrl}get_patient_history/`,
        { patient_id: patientId },
        { headers }
      );

      const [profileResult, historyResult] = await Promise.allSettled([
        diabeticProfilePromise,
        patientHistoryPromise
      ]);

      let mergedHistory = JSON.parse(JSON.stringify(initalMedicalBackgroundData));

      // Process get_patient_history
      if (historyResult.status === "fulfilled" && historyResult.value?.data?.status === "Success") {
        const histories = historyResult.value.data.histories || [];
        let finalFamilyHistory = [],
            finalAllergyHistory = [],
            finalAddictionHistory = [],
            finalDrugHistory = [],
            finalMenstrualHistory = [],
            finalMedicalHistory = [],
            finalSurgicalHistory = [],
            finalImmunizationHistory = [],
            finalHistoryOfPresentIllness = [];

        histories.forEach((entry) => {
          if (entry.medical_history) finalMedicalHistory = finalMedicalHistory.concat(entry.medical_history);
          if (entry.surgical_history) finalSurgicalHistory = finalSurgicalHistory.concat(entry.surgical_history);
          if (entry.family_history) finalFamilyHistory = finalFamilyHistory.concat(entry.family_history);
          if (entry.allergy_history) finalAllergyHistory = finalAllergyHistory.concat(entry.allergy_history);
          if (entry.addiction_history) finalAddictionHistory = finalAddictionHistory.concat(entry.addiction_history);
          if (entry.drug_history) finalDrugHistory = finalDrugHistory.concat(entry.drug_history);
          if (entry.menstrual_history) finalMenstrualHistory = finalMenstrualHistory.concat(entry.menstrual_history);
          if (entry.immunization_history) finalImmunizationHistory = finalImmunizationHistory.concat(entry.immunization_history);
          if (entry.history_of_present_illness) finalHistoryOfPresentIllness = finalHistoryOfPresentIllness.concat(entry.history_of_present_illness);
        });

        mergedHistory = {
          ...mergedHistory,
          "Family History": { prev: finalFamilyHistory, new: [] },
          Allergies: { prev: finalAllergyHistory, new: [] },
          "Addiction History": { prev: finalAddictionHistory, new: [] },
          "Drug History": { prev: finalDrugHistory, new: [] },
          "Menstrual History": { prev: finalMenstrualHistory, new: [] },
          "History of Past Illness": { prev: finalMedicalHistory, new: [] },
          "History of Present Illness": { prev: finalHistoryOfPresentIllness, new: [] },
          Surgeries: { prev: finalSurgicalHistory, new: [] },
          "Adult Vaccination": { prev: finalImmunizationHistory, new: [] },
        };
      }

      if (profileResult.status === "fulfilled") {
        const normalized = normalizeIncomingData(profileResult.value?.data);
        const mergedForm = buildMergedFormData(normalized);
        setFormData(mergedForm);

        // Merge diabetic profile's saved history into new array
        const mapProfileHistory = (key, field) => {
           if (normalized[field] && Array.isArray(normalized[field])) {
               mergedHistory[key] = {
                   ...mergedHistory[key],
                   new: [...mergedHistory[key].new, ...normalized[field]]
               };
           }
        };

        mapProfileHistory("History of Past Illness", "medical_history");
        mapProfileHistory("Family History", "family_history");
        mapProfileHistory("Drug History", "drug_history");
        mapProfileHistory("Allergies", "allergies");
        mapProfileHistory("Addiction History", "addiction_history");
        mapProfileHistory("Surgeries", "surgeries_history");
        mapProfileHistory("Adult Vaccination", "vaccination_history");
        mapProfileHistory("Menstrual History", "menstrual_history");
        mapProfileHistory("History of Present Illness", "history_of_present_illness");

        // Keep legacy support if there was 'history' object 
        if (normalized.history) {
          Object.keys(initalMedicalBackgroundData).forEach(key => {
            if (normalized.history[key] && normalized.history[key].new) {
               mergedHistory[key] = {
                   ...mergedHistory[key],
                   new: [...mergedHistory[key].new, ...normalized.history[key].new]
               };
            } else if (normalized.history[key] && Array.isArray(normalized.history[key])) {
               mergedHistory[key] = {
                   ...mergedHistory[key],
                   new: [...mergedHistory[key].new, ...normalized.history[key]]
               };
            }
          });
        }
      } else {
        // If diabetic profile fetch fails, throw error to catch block
        throw profileResult.reason; 
      }

      setMedicalBackground(mergedHistory);


    } catch (error) {

      setFormData(initialFormData);
      if (error?.response?.status === 404) {
        setFetchError(null);
        toast.info("No existing case sheet found. Fill in and save to create one.");
      } else if (
        error?.response?.status === 401 ||
        error?.response?.status === 403
      ) {
        setFetchError("Session expired. Please log in again.");
      } else if (!navigator.onLine) {
        setFetchError("No internet connection. Please check your network.");
      } else {
        setFetchError("Unable to load case sheet data. Please try again.");
      }
    } finally {
      setIsFetching(false);
    }
  }, [patientId, backendUrl, headers]);

  useEffect(() => {
    if (!open) {
      hasHydratedDraftRef.current = false;
      return;
    }

    const loadData = async () => {
      isDirtyRef.current = false;
      await fetchCaseSheet();

      if (!patientId) {
        hasHydratedDraftRef.current = true;
        return;
      }

      try {
        const draftRecord = await getDraftRecord(CASE_SHEET_DRAFT_KEY);
        const draftPatientId = draftRecord?.patientId;
        const draftData = draftRecord?.data;

        if (!draftData || !draftPatientId) {
          hasHydratedDraftRef.current = true;
          return;
        }

        if (String(draftPatientId) !== String(patientId)) {
          // toast.warn(
          //    "You have a draft saved for another patient. It will be lost for this screen.",
          // );
          await clearDraftRecord(CASE_SHEET_DRAFT_KEY);
          hasHydratedDraftRef.current = true;
          return;
        }

        const applyRestoredDraft = () => {
          setFormData(draftData.formData || initialFormData);
          setMedicalBackground(draftData.medicalBackground || initalMedicalBackgroundData);
          isDirtyRef.current = true;
        };

        const shouldRestore = await showConfirmDialog(
          "A draft case sheet is available for this patient. Do you want to restore it?",
        );
        if (shouldRestore) {
          applyRestoredDraft();
          toast.info("Draft restored.");
        } else {
          const confirmDelete = await showConfirmDialog(
            "If you don't restore this draft case sheet, it will be deleted. Continue?",
          );
          if (confirmDelete) {
            await clearDraftRecord(CASE_SHEET_DRAFT_KEY);
            toast.info("Draft case sheet deleted.");
          } else {
            applyRestoredDraft();
            toast.info("Draft case sheet restored.");
          }
        }
      } catch (error) {
        console.error("Failed to hydrate case sheet draft:", error);
      } finally {
        hasHydratedDraftRef.current = true;
      }
    };

    loadData();
  }, [open, fetchCaseSheet, patientId]);

  
  useEffect(() => {
    if (!hasHydratedDraftRef.current || !patientId || !open) return;

    const intervalId = setInterval(() => {
      if (!isDirtyRef.current) return;
      if (isCaseSheetDraftContentEmpty(formData, medicalBackground)) {
        clearDraftRecord(CASE_SHEET_DRAFT_KEY).catch((error) =>
          console.error("Failed to clear empty case sheet draft:", error),
        );
        return;
      }
      upsertDraftRecord({
        key: CASE_SHEET_DRAFT_KEY,
        patientId: patientId,
        updatedAt: new Date().toISOString(),
        data: {
          formData,
          medicalBackground,
        },
      }).catch((error) =>
        console.error("Failed to auto save case sheet draft:", error),
      );
    }, 30000);

    return () => clearInterval(intervalId);
  }, [patientId, formData, medicalBackground, open]);

  useEffect(() => {
   
    if (open) setActiveSheetTab(SHEET_TAB_DIABETES);
  }, [open]);

  const handleSheetTabChange = (_, next) => {
    if (next) setActiveSheetTab(next);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleTextChange = (key) => (event) => {
    isDirtyRef.current = true;
    setFormData((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleYesNoChange = (key) => (_, nextValue) => {
    if (!nextValue) return;
    isDirtyRef.current = true;
    setFormData((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleLeftRightCheck = (key, side) => (event) => {
    const checked = event.target.checked;
    isDirtyRef.current = true;
    setFormData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [side]: checked },
    }));
  };

  const handleLeftRightText = (side) => (event) => {
    isDirtyRef.current = true;
    setFormData((prev) => ({
      ...prev,
      quantitative_avg_vpt: {
        ...(prev.quantitative_avg_vpt || {}),
        [side]: event.target.value,
      },
    }));
  };

  // ── Persistence (Save Action) ───────────────────────────────────────────────

  const handleSave = async () => {
    if (isSaving) return;
    if (!patientId) {
      toast.error("Patient ID missing. Cannot save case sheet.");
      return;
    }
    if (!backendUrl) {
      toast.error("Backend URL is not configured. Contact support.");
      return;
    }
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    const vpt = formData?.quantitative_avg_vpt || {};
    const vptSideValid = (raw) => {
      if (raw === undefined || raw === null) return true;
      const s = String(raw).trim();
      if (s === "") return true;
      const n = Number(s);
      return Number.isFinite(n);
    };
    if (!vptSideValid(vpt.left) || !vptSideValid(vpt.right)) {
      toast.error(
        "Quantitative Avg. VPT must be a valid number or left blank."
      );
      return;
    }
    setIsSaving(true);
    try {
      const transformedData = normalizeLeftRightPayload(formData);
      
      const payload = {
        ...initialFormData,
        ...transformedData,
        patient_id: patientId,

        // Map frontend structured history components to backend snake_case fields
        // We only send the ".new" array as per backend tracking requirements
        medical_history: medicalBackground["History of Past Illness"]?.new || [],
        history_of_present_illness: medicalBackground["History of Present Illness"]?.new || [],
        family_history: medicalBackground["Family History"]?.new || [],
        drug_history: medicalBackground["Drug History"]?.new || [],
        allergies: medicalBackground["Allergies"]?.new || [],
        addiction_history: medicalBackground["Addiction History"]?.new || [],
        surgeries_history: medicalBackground["Surgeries"]?.new || [],
        vaccination_history: medicalBackground["Adult Vaccination"]?.new || [],
        menstrual_history: medicalBackground["Menstrual History"]?.new || [],
      };

      console.log("Case sheet save payload:", payload);
      const response = await axios.post(
        `${backendUrl}create_patient_diabetic_profile/`,
        payload,
        { headers }
      );

      if (
        response?.data?.status === "Success" ||
        response?.status === 200
      ) {
        toast.success("Case sheet saved successfully.");
        clearDraftRecord(CASE_SHEET_DRAFT_KEY).catch((e) => console.error(e));
        isDirtyRef.current = false;
        if (typeof onAfterSave === "function") onAfterSave(formData);
        onClose?.();
      } else {
        toast.error(
          response?.data?.message || "Unexpected response. Please try again."
        );
      }
    } catch (error) {
      if (
        error?.response?.status === 401 ||
        error?.response?.status === 403
      ) {
        toast.error("Session expired. Please log in again.");
      } else if (error?.response?.status === 422) {
        toast.error(
          "Validation error. Please check all fields and try again."
        );
      } else if (!navigator.onLine) {
        toast.error("No internet connection.");
      } else {
        toast.error(
          error?.response?.data?.message ||
          "Unable to save case sheet. Please try again."
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ── Generic Field Renderers ─────────────────────────────────────────────────
  // These functions return MUI components with consistent styling.
  // They are passed to child tabs as props to ensure UI uniformity.

  // FIX 6: Placeholder text simplified — no "Enter …" prefix, just the field name
  const renderTextField = (label, key, placeholder, multiline = true) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        size="small"
        value={formData[key] ?? ""}
        onChange={handleTextChange(key)}
        placeholder={placeholder}
        fullWidth
        disabled={isFetching}
        autoComplete="off"
        sx={inputSx}
        multiline={multiline}
        minRows={multiline ? 1 : 1}
      />
    </Box>
  );

  const renderSelectField = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <TextField
        select
        size="small"
        value={formData[key] ?? ""}
        onChange={handleTextChange(key)}
        fullWidth
        disabled={isFetching}
        sx={inputSx}
      >
        <MenuItem value="" disabled>
          <em
            style={{ color: "#aaa", fontStyle: "normal", fontSize: 13 }}
          >
            Select…
          </em>
        </MenuItem>
        {(SelectOptions[key] || []).map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const caseSheetToggleButtonGroupSx = {
    gap: 0.5,
    "& .MuiToggleButton-root": {
      border: `1px solid ${BORDER} !important`,
      borderRadius: "8px !important",
      textTransform: "none",
      px: 2.5,
      py: 0.55,
      fontSize: 13,
      fontWeight: 500,
      color: LABEL_COLOR,
      transition: "all 0.15s",
      "&.Mui-selected": {
        backgroundColor: ACCENT,
        color: "#fff",
        borderColor: `${ACCENT} !important`,
        fontWeight: 600,
        "&:hover": { backgroundColor: ACCENT_DARK },
      },
      "&:hover:not(.Mui-selected)": {
        backgroundColor: `${ACCENT}15`,
        borderColor: `${ACCENT} !important`,
        color: ACCENT_DARK,
      },
    },
  };

  const renderYesNoField = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={formData[key] ?? ""}
        onChange={handleYesNoChange(key)}
        disabled={isFetching}
        sx={caseSheetToggleButtonGroupSx}
      >
        <ToggleButton value="Yes">Yes</ToggleButton>
        <ToggleButton value="No">No</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderSkinHydrationField = () => (
    <Box key="skin_hydration" sx={rowSx}>
      <Typography sx={labelSx}>Hydration</Typography>
      <TextField
        select
        size="small"
        value={formData.skin_hydration ?? ""}
        onChange={handleTextChange("skin_hydration")}
        fullWidth
        disabled={isFetching}
        sx={inputSx}
      >
        <MenuItem value="" disabled>
          <em
            style={{ color: "#aaa", fontStyle: "normal", fontSize: 13 }}
          >
            Select Hydration
          </em>
        </MenuItem>
        {(SelectOptions.skin_hydration || []).map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const renderOptionToggleField = (label, key, options) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={formData[key] ?? ""}
        onChange={handleYesNoChange(key)}
        disabled={isFetching}
        sx={caseSheetToggleButtonGroupSx}
      >
        {options.map((opt) => (
          <ToggleButton key={opt} value={opt}>
            {opt}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  const renderLeftRightCheckbox = (label, key) => (
    <Box key={key} sx={rowSx}>
      <Typography sx={labelSx}>{label}</Typography>
      <Box sx={{ display: "flex", gap: 3 }}>
        {["left", "right"].map((side) => (
          <FormControlLabel
            key={side}
            control={
              <Checkbox
                checked={Boolean(formData?.[key]?.[side])}
                onChange={handleLeftRightCheck(key, side)}
                disabled={isFetching}
                size="small"
                sx={{
                  color: BORDER,
                  "&.Mui-checked": { color: ACCENT },
                  p: 0.5,
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: LABEL_COLOR }}
              >
                {side.charAt(0).toUpperCase() + side.slice(1)}
              </Typography>
            }
          />
        ))}
      </Box>
    </Box>
  );

  // ── Patient display info ───────────────────────────────────────────────────
  const patientDisplayName = (() => {
    const name =
      appointment?.patient_name ||
      appointment?.name ||
      appointment?.patientName ||
      appointment?.patient?.name ||
      appointment?.patient?.patient_name ||
      "";
    return String(name).trim();
  })();

  const patientSubtitle = patientId
    ? `Patient ID: ${patientId}`
    : "—";

  const saveDisabled = isSaving || isFetching || Boolean(fetchError);

  const saveButtonSx = (placement) => ({
    textTransform: "none",
    borderRadius: "8px",
    backgroundColor: "#213360",
    fontSize: 13,
    fontWeight: 600,
    px: 2.5,
    py: 0.75,
    minHeight: 36,
    minWidth: { xs: 104, sm: 112 },
    boxShadow: "none",
    "&:hover": { backgroundColor: ACCENT_DARK, boxShadow: "none" },
    ...(placement === "header"
      ? {
        "&.Mui-disabled": {
          backgroundColor: "rgba(255,255,255,0.2)",
          color: "rgba(255,255,255,0.45)",
        },
      }
      : {
        "&.Mui-disabled": {
          backgroundColor: "#e0e0e0",
          color: "#9e9e9e",
        },
      }),
  });

  const renderSaveButton = (placement) => (
    <Tooltip title={isSaving ? "Saving…" : "Save case sheet"} arrow>
      <span>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveDisabled}
          sx={saveButtonSx(placement)}
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </span>
    </Tooltip>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        if (!isSaving) onClose?.();
      }}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 64px rgba(33,51,96,0.22)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: PAYMENT_HEADER_BG,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          py: 1.5,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
          <Typography
            component="div"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: { xs: 16, sm: 18 },
              lineHeight: 1.25,
            }}
          >
            Case Sheet / Diabetes Profile
          </Typography>
          <Typography
            component="div"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 500,
              fontSize: { xs: 13, sm: 14 },
              mt: 0.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={patientSubtitle}
          >
            {patientSubtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSaving}
          sx={{
            color: "#FFFFFF",
            flexShrink: 0,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: SURFACE,
        }}
      >
        {isFetching && (
          <Box
            sx={{
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              gap: 2,
              minHeight: 280,
            }}
          >
            <CircularProgress size={36} sx={{ color: ACCENT }} />
            <Typography sx={{ color: LABEL_COLOR, fontSize: 13 }}>
              Loading case sheet…
            </Typography>
          </Box>
        )}

        {!isFetching && fetchError && (
          <Box sx={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
            <FetchErrorState message={fetchError} onRetry={fetchCaseSheet} />
          </Box>
        )}

        {!isFetching && !fetchError && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 auto",
              minHeight: 0,
              mx: 0,
              mt: 0,
            }}
          >
            <Tabs
              value={activeSheetTab}
              onChange={handleSheetTabChange}
              variant="fullWidth"
              textColor="inherit"
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                flexShrink: 0,
                minHeight: 44,
                backgroundColor: "#FFFFFF",
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTabs-flexContainer": { gap: "0px", height: "100%" },
              }}
            >
              {[
                { value: SHEET_TAB_DIABETES, label: "Diabetes" },
                { value: SHEET_TAB_FOOT, label: "Diabetic Foot" },
                { value: SHEET_TAB_HISTORY, label: "History" },
              ].map(({ value, label }) => (
                <Tab
                  key={value}
                  label={label}
                  value={value}
                  disableRipple
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    minHeight: 44,
                    flex: 1,
                    borderBottom:
                      activeSheetTab === value
                        ? `3px solid ${ACCENT}`
                        : "3px solid transparent",
                    bgcolor:
                      activeSheetTab === value
                        ? `${ACCENT}10`
                        : "#FFFFFF",
                    color:
                      activeSheetTab === value ? ACCENT_DARK : "#555",
                    transition: "all 0.2s ease-in-out",
                    borderRadius: 0,
                    "&:hover": {
                      bgcolor: `${ACCENT}08`,
                      color: ACCENT_DARK,
                    },
                  }}
                />
              ))}
            </Tabs>

            <Box
              sx={{
                flexShrink: 0,
                px: 2.5,
                py: 1.5,
                backgroundColor: "#F9FAFB",
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#4A5568",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {(() => {
                  const name = displayPatientName(
                    patientData?.name ||
                      patientData?.patient_name ||
                      patientDisplayName,
                    "—"
                  );
                  const gender = patientData?.gender || "—";
                  const age = patientData?.age ?? null;
                  const ageStr = age !== null && age !== undefined ? `${age} years` : "—";
                  return `Patient Name: ${name}, Sex: ${gender}, Age: ${ageStr}`;
                })()}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                maxHeight: { xs: "calc(92vh - 200px)", sm: "calc(92vh - 180px)" },
                overflowY: "auto",
                p: { xs: 2, sm: 2.5 },
                pb: 3,
                WebkitOverflowScrolling: "touch",
                backgroundColor: SURFACE,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: `${BRAND}40`,
                  borderRadius: 4,
                  "&:hover": { background: `${BRAND}75` },
                },
              }}
            >

              {activeSheetTab === SHEET_TAB_DIABETES && (
                <DiabetesTab
                formData={formData}
                setFormData={setFormData}
                isFetching={isFetching}
                renderTextField={renderTextField}
                renderYesNoField={renderYesNoField}
                renderSelectField={renderSelectField}
                renderOptionToggleField={renderOptionToggleField}
                renderSkinHydrationField={renderSkinHydrationField}
                />
              )}

              {activeSheetTab === SHEET_TAB_FOOT && (
                <DiabeticFootTab
                formData={formData}
                setFormData={setFormData}
                isFetching={isFetching}
                renderTextField={renderTextField}
                renderSelectField={renderSelectField}
                renderLeftRightCheckbox={renderLeftRightCheckbox}
                handleLeftRightText={handleLeftRightText}
                />
              )}
              {activeSheetTab === SHEET_TAB_HISTORY && (
                <HistoryTab
                  formData={formData}
                  isFetching={isFetching}
                  renderTextField={renderTextField}
                  renderYesNoField={renderYesNoField}
                  renderSelectField={renderSelectField}
                  renderOptionToggleField={renderOptionToggleField}
                  medicalBackground={medicalBackground}
                  updateMedicalBackground={updateMedicalBackground}
                  gender={patientData?.gender}
                />
              )}


            </Box>

          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          justifyContent: "center",
          px: 2,
          py: 1.5,
          minHeight: 56,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            minHeight: 40,
          }}
        >
          {!isFetching && !fetchError ? renderSaveButton("footer") : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default CaseSheetModal;